///
/// EarthBrowser
///
/// Created by Matt Giger on 1/24/2024
/// Copyright (c) 2024 Geodata Labs
///

export const c_1970_epoch_offset		= 2440587.5
export const c_jd_2000					= 2451545.0

export type time_element = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second'

export interface time_delta_value {
	type: time_element
	value: number
}

/**
 * Represents a time class for handling various time-related operations.
 */
export class time {
	/** Represents a time far in the past */
	public static longAgo: time = new time(-1e7)
	/** Represents a time far in the future */
	public static farInTheFuture: time = new time(1e7)

	/** The Julian date representation of the time */
	public readonly julian: number
	/** The JavaScript Date object representation of the time */
	public readonly date: Date

	/**
	 * Creates a new time instance.
	 * @param value - The input value to create the time from (Date, number, or string)
	 */
	constructor(value: Date | number | string = new Date()) {

		// convert UTC time string to julian time
		if(typeof value === "string") {
			let dstring = value.replace(' ', 'T')
			if(dstring.endsWith('+00')) {
				dstring = dstring.replace('+00', 'Z')
			} else if(dstring.endsWith('-00')) {
				dstring = dstring.replace('-00', 'Z')
			} else if(/[+-]\d{2}$/.test(dstring)) {
				dstring = dstring.replace(/([+-]\d{2})$/, '$1:00')
			} else  if(!dstring.endsWith('Z')) {
				dstring += 'Z'
			}
			value = new Date(dstring)
		}

		// convert Unix epoch time to julian time
		if(value instanceof Date) {
			this.julian = value.getTime() / 86400000 + c_1970_epoch_offset
			this.date = value

		} else if(typeof value === "number") {
			 this.julian = value

			 // convert julian time to Unix epoch time
			 this.date = new Date((this.julian - c_1970_epoch_offset) * 86400000)
		} else {
			throw new Error("Invalid time value")
		}
	}

	public elementLocaleValue(element: time_element): number {
		switch(element) {
			case 'year': return this.date.getFullYear()
			case 'month': return this.date.getMonth() + 1
			case 'day': return this.date.getDate()
			case 'hour': return this.date.getHours()
			case 'minute': return this.date.getMinutes()
			case 'second': return this.date.getSeconds()
			default:
				throw new Error(`Invalid time element: ${element}`)
		}
	}

	public withElementLocaleValue(element: time_element, value: number): time {
		const d = new Date(this.date.getTime())
		switch(element) {
			case 'year':
				d.setFullYear(value)
				break
			case 'month':
				d.setMonth(value - 1)
				break
			case 'day':
				d.setDate(value)
				break
			case 'hour':
				d.setHours(value)
				break
			case 'minute':
				d.setMinutes(value)
				break
			case 'second':
				d.setSeconds(value)
				break
			default:
				throw new Error(`Invalid time element: ${element}`)
		}
		return new time(d)
	}

	public static elementRange(element: time_element): [number, number] {
		switch(element) {
			case 'year': return [1900, 2100]
			case 'month': return [1, 12]
			case 'day': return [1, 31]
			case 'hour': return [0, 23]
			case 'minute': return [0, 59]
			case 'second': return [0, 59]
			default:
				throw new Error(`Invalid time element: ${element}`)
		}
	}

	/**
	 * Gets the day of the week (0-6, where 0 is Sunday).
	 */
	public get wkday(): number {
		return Math.floor(this.julian + 1.5) % 7
	}

	/**
	 * Gets the start of the year for this time.
	 */
	public get yearStart(): time {
		const d = new Date()
		d.setUTCFullYear(this.date.getUTCFullYear(), 0, 1)
		d.setUTCHours(0, 0, 0, 0)
		return new time(d)
	}

	/**
	 * Gets the start of the day for this time.
	 */
	public get dayStart(): time {
		const d = new Date(this.date.getTime())
		d.setUTCHours(0, 0, 0, 0)
		return new time(d)
	}

	/**
	 * Checks if this time is less than another time.
	 * @param other - The time to compare against
	 * @returns True if this time is less than the other time
	 */
	public less(other: time): boolean {
		return this.julian < other.julian
	}

	/**
	 * Checks if this time is less than or equal to another time.
	 * @param other - The time to compare against
	 * @returns True if this time is less than or equal to the other time
	 */
	public lessOrEqual(other: time): boolean {
		return this.julian <= other.julian
	}

	/**
	 * Checks if this time is greater than another time.
	 * @param other - The time to compare against
	 * @returns True if this time is greater than the other time
	 */
	public greater(other: time): boolean {
		return this.julian > other.julian
	}

	/**
	 * Checks if this time is greater than or equal to another time.
	 * @param other - The time to compare against
	 * @returns True if this time is greater than or equal to the other time
	 */
	public greaterOrEqual(other: time): boolean {
		return this.julian >= other.julian
	}

	/**
	 * Checks if this time is equal to another time.
	 * @param other - The time to compare against
	 * @returns True if this time is equal to the other time
	 */
	public equals(other: time): boolean {
		return this.julian == other.julian
	}

	/**
	 * Calculates the difference between this time and another time.
	 * @param other - The time to calculate the difference from
	 * @returns A timedelta representing the difference
	 */
	public difference(other: time): timedelta {
		const delta = (this.julian - other.julian) * 86400.0
		return new timedelta([{type: 'second', value: delta}])
	}

	/**
	 * Returns a string representation of this time.
	 */
	public toString(): string {
		return this.date.toString()
	}

	/**
	 * Returns a locale-specific string representation of this time.
	 */
	public toLocaleString(): string {
		return this.date.toLocaleString()
	}

	/**
	 * Gets the abbreviated weekday name.
	 */
	public get weekdayAbbr(): string {
		return time.weekdayFormat.format(this.date)
	}

	/**
	 * Gets the two-digit hour representation.
	 */
	public get twoDigitHour(): string {
		return time.twoDigitHourFormat.format(this.date)
	}

	/**
	 * Gets the two-digit minute representation.
	 */
	public get twoDigitMinute(): string {
		return time.twoDigitMinuteFormat.format(this.date)
	}

	/**
	 * Gets the hour and minute representation.
	 */
	public get hourMinute(): string {
		return time.hourMinuteFormat.format(this.date)
	}

	/**
	 * Gets the month and day representation.
	 */
	public get monthDay(): string {
		return time.monthDayFormat.format(this.date)
	}

	/**
	 * Greenwich mean sidereal time.
	 */
	public get gstime(): number {
		return (18.697375 + 24.065709824279 * this.julian) % 24
	}

	/**
	 * Adds a timedelta to this time.
	 * @param delta - The timedelta to add
	 * @returns A new time instance with the timedelta added
	 */
	public adding(delta: timedelta): time {
		const d = new Date(this.date.getTime())
		delta.elements.forEach(element => {
			const sign = element.value > 0 ? 1 : -1
			const count = Math.abs(element.value)

			switch(element.type) {
				case 'year': {
					d.setUTCFullYear(d.getUTCFullYear() + sign * count)
					break
				}
				case 'month': {
					for(let i = 0; i < count; i++) {
						if(d.getUTCMonth() == 12 && sign > 0) {
							d.setUTCFullYear(d.getUTCFullYear() + 1)
							d.setUTCMonth(1)
						} else if(d.getUTCMonth() == 1 && sign < 0) {
							d.setUTCFullYear(d.getUTCFullYear() - 1)
							d.setUTCMonth(12)
						} else {
							d.setUTCMonth(d.getUTCMonth() + sign)
						}
					}
					break
				}
				case 'day': {
					d.setTime(d.getTime() + sign * count * 24 * 60 * 60 * 1000)
					break
				}
				case 'hour': {
					d.setTime(d.getTime() + sign * count * 60 * 60 * 1000)
					break
				}
				case 'minute': {
					d.setTime(d.getTime() + sign * count * 60 * 1000)
					break
				}
				case 'second': {
					d.setTime(d.getTime() + sign * count * 1000)
					break
				}
			}
		})
		return new time(d)
	}

	/** The locale used for formatting */
	private static locale: string = Intl.NumberFormat().resolvedOptions().locale
	/** Formatter for weekday abbreviations */
	private static weekdayFormat = new Intl.DateTimeFormat(time.locale, { weekday: 'short' })
	/** Formatter for two-digit hours */
	private static twoDigitHourFormat = new Intl.DateTimeFormat(time.locale, { hour12: false, hour: '2-digit' })
	/** Formatter for two-digit minutes */
	private static twoDigitMinuteFormat = new Intl.DateTimeFormat(time.locale, { minute: '2-digit' })
	/** Formatter for hour and minute */
	private static hourMinuteFormat = new Intl.DateTimeFormat(time.locale, { hour12: false, hour: '2-digit', minute: '2-digit' })
	/** Formatter for month and day */
	private static monthDayFormat = new Intl.DateTimeFormat(time.locale, { month: '2-digit', day: '2-digit' })
}

/**
 * Represents a time difference or duration.
 */
export class timedelta {
	public static years(value: number = 1): timedelta { return new timedelta([{ type: 'year', value }]) }
	public static months(value: number = 1): timedelta { return new timedelta([{ type: 'month', value }]) }
	public static days(value: number = 1): timedelta { return new timedelta([{ type: 'day', value }]) }
	public static hours(value: number = 1): timedelta { return new timedelta([{ type: 'hour', value }]) }
	public static minutes(value: number = 1): timedelta { return new timedelta([{ type: 'minute', value }]) }
	public static seconds(value: number = 1): timedelta { return new timedelta([{ type: 'second', value }]) }

	public readonly elements: time_delta_value[] = []
	public readonly secondsDelta: number
	public readonly jdelta: number

	constructor(elements: time_delta_value[] = []) {
		this.elements = elements

		// ignore years and months
		let delta = 0
		this.elements.forEach(element => {
			switch(element.type) {
				case 'year':	break
				case 'month':	break
				case 'day':
					delta += element.value * 86400
					break
				case 'hour':
					delta += element.value * 3600
					break
				case 'minute':
					delta += element.value * 60
					break
				case 'second':
					delta += element.value
			}
		})
		this.secondsDelta = delta
		this.jdelta = delta / 86400.0
	}
}

/**
 * Represents a range of time with a start and end.
 */
export class timerange {
	/** The start time of the range */
	public start: time
	/** The end time of the range */
	public end: time

	/**
	 * Creates a new timerange instance.
	 * @param start - The start time of the range (default: time.longAgo)
	 * @param end - The end time of the range (default: time.farInTheFuture)
	 */
	constructor(start: time = time.longAgo, end: time = time.farInTheFuture) {
		this.start = start
		this.end = end
	}

	/**
	 * Checks if a given time is within the range.
	 * @param time - The time to check
	 * @returns True if the time is within the range, false otherwise
	 */
	public contains(time: time): boolean {
		return this.start.lessOrEqual(time) && this.end.greaterOrEqual(time)
	}

	/**
	 * Gets the duration of the time range.
	 * @returns A timedelta representing the duration of the range
	 */
	public get range(): timedelta {
		return this.end.difference(this.start)
	}
}